import { FC, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import {
  AccessTime, PersonOutline, PlaceOutlined, PhoneOutlined, WarningAmber, StickyNote2Outlined,
} from '@mui/icons-material';
import { DateTime } from 'luxon';
import type { Barn } from '../../services/barnsService';
import { getAppointmentSubject } from './subjectLabel';
import { statusStyle } from './appointmentStatus';

interface Contact {
  name: string;
  phone?: string | null;
}

export interface HoverCardAppointment {
  date: string;
  duration?: number;
  type: string;
  status?: string;
  location: string;
  notes?: string | null;
  barnId?: string | null;
  assignedVet?: { name: string } | null;
  horse?: {
    name: string;
    patientId?: string | null;
    barnId?: string | null;
    /** The barn NAME (INV-42 keeps it in step with barnId), so a farm call can name the barn offline. */
    location?: string | null;
    owners?: Contact[] | null;
    medicalAlerts?: any[] | null;
  } | null;
  client?: Contact | null;
  barn?: { id: string; name: string } | null;
}

/**
 * Which barn's address belongs on the card: the appointment's own barn, else for a farm call the
 * barn the horse lives at. A clinic visit has no address to drive to.
 */
export const resolveVisitBarnId = (
  appointment: Pick<HoverCardAppointment, 'location' | 'barnId' | 'horse'>,
): string | null =>
  appointment.barnId ||
  (appointment.location?.toLowerCase() === 'farm' ? appointment.horse?.barnId ?? null : null) ||
  null;

/** A patient visit that is neither at a barn nor a farm call (at the clinic), so where the horse lives is worth showing. */
const awayFromHome = (appointment: Pick<HoverCardAppointment, 'location' | 'barnId' | 'horse'>) =>
  !!appointment.horse && !resolveVisitBarnId(appointment) && appointment.location?.toLowerCase() !== 'farm';

/** The barn the horse lives at, for such a visit. */
export const resolveHomeBarnId = (
  appointment: Pick<HoverCardAppointment, 'location' | 'barnId' | 'horse'>,
): string | null => (awayFromHome(appointment) ? appointment.horse?.barnId ?? null : null);

export const barnAddressLines = (barn: Barn) => {
  const cityLine = [barn.city, [barn.state, barn.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [barn.street, cityLine].filter(Boolean) as string[];
};

const barnAddress = (barn: Barn) => barnAddressLines(barn).join(', ');

/**
 * Where the horse lives, for such a visit: its barn with the saved address, else the location text.
 * With no street or city saved, maps search that text (Ali, 2026-10-22).
 */
export const homePlace = (
  appointment: Pick<HoverCardAppointment, 'location' | 'barnId' | 'horse'>,
  homeBarn?: Barn | null,
): { name: string; addressLines: string[]; mapQuery: string } | null => {
  if (!awayFromHome(appointment)) return null;
  const name = homeBarn?.name || appointment.horse?.location;
  if (!name) return null;
  const addressLines = homeBarn ? barnAddressLines(homeBarn) : [];
  return { name, addressLines, mapQuery: addressLines.join(', ') || name };
};

const Row: FC<{ icon: ReactNode; children: ReactNode }> = ({ icon, children }) => (
  <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
    <Box sx={{ color: 'text.secondary', display: 'flex', pt: '2px' }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

const iconSx = { fontSize: 16 };

/**
 * What a front-desk person needs without opening the appointment: when, who, which vet, where to
 * drive and who to call. Times are in the browser's zone, like the calendar blocks it sits on.
 */
const AppointmentHoverCard: FC<{
  appointment: HoverCardAppointment;
  barn?: Barn | null;
  /** The barn the horse lives at, for a visit at the clinic. */
  homeBarn?: Barn | null;
  /** A grouped barn visit: its horses, listed under the barn. */
  visitHorses?: Array<{ name: string; type: string; alert?: string; time?: string }>;
}> = ({ appointment, barn, homeBarn, visitHorses }) => {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const start = DateTime.fromISO(appointment.date, { zone: 'utc' }).setZone(zone);
  const minutes = appointment.duration || 60;
  const end = start.plus({ minutes });
  const subject = getAppointmentSubject(appointment);
  const location = appointment.location?.toLowerCase();
  const barnId = resolveVisitBarnId(appointment);

  const place = barn
    ? { title: barn.name, detail: barnAddress(barn) }
    : barnId
      ? {
          title: appointment.barnId
            ? appointment.barn?.name || appointment.location
            : appointment.horse?.location || 'Farm call',
          detail: '',
        }
      : location === 'clinic'
        ? { title: 'At the clinic', detail: '' }
        : location === 'farm'
          ? { title: 'Farm call', detail: 'No address on file' }
          : { title: appointment.location || 'No location', detail: '' };
  const home = homePlace(appointment, homeBarn);

  const contacts: Array<{ label: string; phone?: string | null }> = [];
  if (barn?.contactName || barn?.contactPhone) {
    contacts.push({ label: `${barn.contactName || 'Barn'} · Barn contact`, phone: barn.contactPhone });
  }
  const owners = Array.isArray(appointment.horse?.owners) ? appointment.horse!.owners! : [];
  owners.slice(0, 2).forEach((o) => contacts.push({ label: `${o.name} · Owner`, phone: o.phone }));
  if (!appointment.horse && appointment.client) {
    contacts.push({ label: `${appointment.client.name} · Owner`, phone: appointment.client.phone });
  }

  const alerts = (appointment.horse?.medicalAlerts ?? [])
    .map((a: any) => ({ text: typeof a === 'string' ? a : a?.text, critical: !!a?.isCritical }))
    .filter((a: { text?: string }) => !!a.text)
    .sort((a: { critical: boolean }, b: { critical: boolean }) => Number(b.critical) - Number(a.critical));

  const durationLabel = minutes < 60 ? `${minutes} min` : minutes % 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes / 60} hr`;
  const status = appointment.status ? statusStyle(appointment.status).label : '';

  return (
    <Box sx={{ width: 320, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.3 }}>
          {subject.name}
          {appointment.horse?.patientId && (
            <Box component="span" sx={{ ml: 1, fontSize: '0.8rem', fontWeight: 500, color: 'text.secondary' }}>
              {appointment.horse.patientId}
            </Box>
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {visitHorses
            ? `${visitHorses.length} horse${visitHorses.length === 1 ? '' : 's'} · Barn visit`
            : [appointment.type, status].filter(Boolean).join(' · ')}
        </Typography>
      </Box>

      <Row icon={<AccessTime sx={iconSx} />}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {`${start.toFormat('ccc, LLL d')} · ${start.toFormat('h:mm')} - ${end.toFormat('h:mm a')}`}
        </Typography>
        <Typography variant="caption" color="text.secondary">{durationLabel}</Typography>
      </Row>

      <Row icon={<PersonOutline sx={iconSx} />}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {appointment.assignedVet?.name || 'Unassigned'}
        </Typography>
      </Row>

      <Row icon={<PlaceOutlined sx={iconSx} />}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{place.title}</Typography>
        {place.detail && <Typography variant="caption" color="text.secondary">{place.detail}</Typography>}
        {home && (
          <>
            <Typography variant="caption" component="div" color="text.secondary">{`Lives at ${home.name}`}</Typography>
            {home.addressLines.length > 0 && (
              <Typography variant="caption" component="div" color="text.secondary">{home.addressLines.join(', ')}</Typography>
            )}
          </>
        )}
      </Row>

      {barn?.notes && (
        <Row icon={<StickyNote2Outlined sx={iconSx} />}>
          <Typography
            variant="body2"
            sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}
          >
            {barn.notes}
          </Typography>
          <Typography variant="caption" color="text.secondary">Barn notes</Typography>
        </Row>
      )}

      {contacts.map((c) => (
        <Row key={c.label} icon={<PhoneOutlined sx={iconSx} />}>
          {c.phone && <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.phone}</Typography>}
          <Typography variant="caption" color="text.secondary">{c.label}</Typography>
        </Row>
      ))}

      {visitHorses && (
        <Box sx={{ pt: 1.25, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>
            Horses on this visit
          </Typography>
          {visitHorses.map((h, i) => (
            <Box key={`${h.name}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {h.time && (
                <Typography variant="body2" color="text.secondary" sx={{ flex: 'none', width: 64 }}>{h.time}</Typography>
              )}
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{h.name}</Typography>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>{h.type}</Typography>
              {h.alert && (
                <Box component="span" title={h.alert} sx={{ display: 'inline-flex', color: 'error.main' }}>
                  <WarningAmber sx={iconSx} aria-label={`Medical alert: ${h.alert}`} />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {alerts.map((a: { text: string; critical: boolean }, i: number) => (
        <Row key={i} icon={<WarningAmber sx={{ ...iconSx, color: a.critical ? 'error.main' : 'warning.main' }} />}>
          <Typography variant="body2" sx={{ color: a.critical ? 'error.dark' : 'text.primary', fontWeight: 500 }}>
            {a.text}
          </Typography>
        </Row>
      ))}

      {appointment.notes && (
        <Row icon={<StickyNote2Outlined sx={iconSx} />}>
          <Typography
            variant="body2"
            sx={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}
          >
            {appointment.notes}
          </Typography>
        </Row>
      )}
    </Box>
  );
};

export default AppointmentHoverCard;
